import React from 'react';
import { KeyStore } from 'store/key/key-store';
import { AccountStore } from 'store/account-store';
import { usePromiseMemo } from 'utils/common-utils';
import { IClientPspEncryptionType, IKeyTypeSupport, KeyEncryptionType, PSP } from 'dto/key-management/key-dto';
import { useAlertMessages } from '../components/alert/use-messages';
import { getErrorMessage } from 'common-utils';

export interface IPspStates {
    pspEncryptionTypeMap: Map<PSP, KeyEncryptionType>;
    selectedPsp: string;
    supportedKeyTypes: IKeyTypeSupport[];
    updateSelectedPsp: (value: PSP) => void;
    isLoading?: boolean;
    errorMessage?: string;
}
export interface IGetpspTypesForClientOptions {
    supportPspTypes?: PSP[];
    fullyKeyConfigured?: boolean;
}

export const pspTypesForClient = (accountStore: AccountStore, keyStore: KeyStore, clientId: number, options?: IGetpspTypesForClientOptions): IPspStates => {
    const [selectedPspType, setSelectedPspType] = React.useState<PSP>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const { errorMessages, addErrorMessages, clearErrorMessages } = useAlertMessages();
    const allSupportedKeyTypes = usePromiseMemo({}, () => {
        return keyStore.getSupportedKeyTypes();
    }, []);

    const supportedKeyTypes = React.useMemo(() => {
        if (!selectedPspType) {
            return [];
        }
        return allSupportedKeyTypes[selectedPspType] ?? [];
    }, [selectedPspType, allSupportedKeyTypes]);

    const pspEncryptionTypes = usePromiseMemo<IClientPspEncryptionType[]>(
        [],
        async () => {
            clearErrorMessages();
            setLoading(true);
            try {
                setSelectedPspType(undefined);
                const account = accountStore.getAccountById(clientId);
                if (!account) {
                    return [];
                }
                const pspEncryptionTypesOfAccount = await keyStore.getPspEncryptionTypesOfAccount(account.uuid, options?.fullyKeyConfigured);
                const defaultSupportedPspTypes: PSP[] = [PSP.LittlePay, PSP.Pulse, PSP.Unicard, PSP.ExternalKeyManager];
                const supportPspTypes = options?.supportPspTypes ?? defaultSupportedPspTypes;
                return pspEncryptionTypesOfAccount.filter(item => supportPspTypes.includes(item.pspType));
            } catch (err) {
                addErrorMessages(getErrorMessage(err));
                return [];
            } finally {
                setLoading(false);
            }
        },
        [clientId],
    );
    const pspEncryptionTypeMap = new Map(pspEncryptionTypes.map(value => [value.pspType, value.encryptionType??null]));
    return {
        pspEncryptionTypeMap,
        selectedPsp: selectedPspType,
        supportedKeyTypes,
        updateSelectedPsp: setSelectedPspType,
        errorMessage: errorMessages[0],
        isLoading: loading,
    };
};
